/**
 * PATH: packages/ui/src/kanban/components/kanbanTouchGesture.ts
 * FILE_ID: UI-KANBAN-TOUCH-GESTURE-001
 * PURPOSE: The one place the card's touch gesture budget is defined — how long
 *   a finger must rest before a hold means something, and how far it may move
 *   before the hold is really a scroll.
 * WHY:
 *   A single stationary finger on a card is claimed by THREE features in
 *   sequence (scroll → drag → hold), and until this module existed the
 *   handover points were literals in two files that had no idea about each
 *   other: `useTouchDragCard` declared `MENU_TAKEOVER_MS = 500` and
 *   `useKanbanCardInteractions` separately hard-coded `setTimeout(…, 500)`.
 *   The drag hook's own header calls that pair a "LANDMINE". They agreed only
 *   by coincidence, and the moment one moved, the gesture would either fire
 *   twice (hold acts while the drag still believes it owns the finger) or not
 *   at all. Deriving the handover from one exported constant makes that class
 *   of drift unrepresentable.
 * FLOW:
 *   ┌──────────────┐        ┌───────────────┐        ┌──────────────────┐
 *   │ 0ms          │  hold  │ DRAG_HOLD_MS  │  hold  │ LONG_PRESS_MS    │
 *   │ move = scroll│ ─────▶ │ move = drag   │ ─────▶ │ hold acts; drag  │
 *   │ (stand down) │        │               │        │ cedes the finger │
 *   └──────────────┘        └───────────────┘        └──────────────────┘
 * DEPENDENCIES: none — plain constants, so both the drag hook and the
 *   interaction hook can import without a cycle.
 * RELATED:
 *   - ./useKanbanCardInteractions.ts: owns the hold timer.
 *   - ./lane/useTouchDragCard.ts: cedes the gesture at LONG_PRESS_MS.
 */

/**
 * How long a stationary finger must rest on a card before the hold is treated
 * as deliberate, ms.
 *
 * 450ms, down from the 500ms that was duplicated across the two hooks. 500ms
 * is the iOS/Android system long-press floor, which is the right number when
 * the gesture competes with the platform's own text-selection hold; here it
 * competes only with our drag, so the shorter dwell reads as more responsive
 * without becoming reachable by accident. It must stay strictly ABOVE
 * TOUCH_DRAG_HOLD_MS — see the ordering note there.
 */
export const LONG_PRESS_MS = 450

/**
 * Movement past this (px) means the finger is travelling, not resting.
 *
 * Shared by the hold timer and the drag's slop check ON PURPOSE: if the two
 * disagreed, a drift of a few pixels could cancel one gesture while arming the
 * other, and the card would neither drag nor open its actions.
 */
export const TOUCH_MOVE_CANCEL_PX = 10
